/**
 * Iter 76 Phase 3 — Share modal for the Sheet Viewer.
 *
 * Triggered by the Share button in the viewer toolbar. Lets a user
 * mint a controlled share link for the current sheet (with options
 * to also share the current view state, markups, measurements,
 * objects, photos, and documents). The recipient receives a tokenized
 * public URL — never a tenant API key.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Link as LinkIcon, Copy, Check, Lock, ExternalLink, RefreshCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const DURATIONS = [
  { id: 24, label: "24 hours" },
  { id: 24 * 7, label: "7 days" },
  { id: 24 * 30, label: "30 days" },
];

export default function ShareModal({ projectId, sheetId, viewState, onClose }) {
  const [shareType, setShareType] = useState("sheet"); // sheet | viewer_state | plan_set | selected_sheets
  const [title, setTitle] = useState("Shared sheet");
  const [description, setDescription] = useState("");
  const [includeMarkups, setIncludeMarkups] = useState(true);
  const [includeMeasurements, setIncludeMeasurements] = useState(true);
  const [includeObjects, setIncludeObjects] = useState(true);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [includeDocuments, setIncludeDocuments] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [duration, setDuration] = useState(24);
  const [customHours, setCustomHours] = useState(null);
  // Iter 76.1 — 3-way access mode: public_token | password | email_allowlist.
  const [accessMode, setAccessMode] = useState("public_token");
  const [password, setPassword] = useState("");
  const [emailListRaw, setEmailListRaw] = useState("");

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // {token, id, ...}
  const [copied, setCopied] = useState(false);
  const [shares, setShares] = useState([]);
  const [emailErrors, setEmailErrors] = useState([]);

  // Parse + validate the comma-separated allowlist.
  const emailList = useMemo(() => {
    const raw = (emailListRaw || "").split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    return Array.from(new Set(raw.map((e) => e.toLowerCase())));
  }, [emailListRaw]);
  const validateEmails = (list) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return list.filter((e) => !re.test(e));
  };

  const reload = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/projects/${projectId}/plan-shares`);
      setShares(r.data || []);
    } catch { setShares([]); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const shareUrl = useMemo(() => {
    if (!created?.token) return "";
    const base = window.location.origin;
    return `${base}/plan-share/${created.token}`;
  }, [created]);

  const submit = async () => {
    if (!projectId) return;
    // Client-side gate validation.
    if (accessMode === "password" && (password || "").length < 4) {
      window.alert("Password must be at least 4 characters.");
      return;
    }
    let bad = [];
    if (accessMode === "email_allowlist") {
      if (emailList.length === 0) {
        window.alert("Add at least one recipient email.");
        return;
      }
      bad = validateEmails(emailList);
      if (bad.length > 0) {
        setEmailErrors(bad);
        window.alert(`Invalid email(s): ${bad.join(", ")}`);
        return;
      }
      setEmailErrors([]);
    }
    setCreating(true);
    try {
      const body = {
        share_type: shareType,
        title: title.trim() || "Shared sheet",
        description: description.trim() || null,
        sheet_ids: shareType === "viewer_state" || shareType === "sheet" ? (sheetId ? [sheetId] : []) : [],
        current_view_state: shareType === "viewer_state" ? viewState : null,
        include_markups: includeMarkups,
        include_measurements: includeMeasurements,
        include_objects: includeObjects,
        include_photos: includePhotos,
        include_documents: includeDocuments,
        allow_download: allowDownload,
        expires_in_hours: customHours || duration,
        access_mode: accessMode,
        password: accessMode === "password" ? password : null,
        email_allowlist: accessMode === "email_allowlist" ? emailList : null,
        require_email: accessMode === "email_allowlist",
      };
      const r = await api.post(`/projects/${projectId}/plan-shares`, body);
      setCreated(r.data);
      reload();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Failed to create share");
    } finally { setCreating(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link? Recipients lose access immediately.")) return;
    try {
      await api.delete(`/plan-shares/${id}`);
      reload();
      if (created?.id === id) setCreated(null);
    } catch (e) { window.alert(e?.response?.data?.detail || "Failed to revoke"); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-start justify-center pt-12 px-4 overflow-y-auto"
         onClick={onClose} data-testid="share-modal">
      <div className="w-full max-w-lg bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--fop-border)]">
          <div className="flex items-center gap-2">
            <LinkIcon size={16} className="text-[var(--fop-primary)]" />
            <span className="font-display text-[var(--fop-text)] text-base">Share plans</span>
          </div>
          <button onClick={onClose} data-testid="share-close" className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] w-11 h-11 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        {!created ? (
          <div className="p-4 space-y-4 text-sm text-[var(--fop-text)]">
            <Section title="Scope">
              <div className="grid grid-cols-2 gap-2">
                <Choice on={shareType === "sheet"} onClick={() => setShareType("sheet")} label="Current sheet" testid="share-scope-sheet" />
                <Choice on={shareType === "viewer_state"} onClick={() => setShareType("viewer_state")} label="Current view" testid="share-scope-view" />
                <Choice on={shareType === "plan_set"} onClick={() => setShareType("plan_set")} label="Whole plan set" testid="share-scope-plan-set" />
                <Choice on={shareType === "selected_sheets"} onClick={() => setShareType("selected_sheets")} label="Selected sheets" testid="share-scope-selected" />
              </div>
            </Section>

            <Section title="Details">
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="Title shown to the recipient"
                     data-testid="share-title"
                     className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm" />
              <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional message" data-testid="share-description"
                        className="w-full mt-2 bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm" />
            </Section>

            <Section title="Include">
              <Toggle on={includeMarkups} onClick={() => setIncludeMarkups((v) => !v)} label="Markups" testid="share-incl-markups" />
              <Toggle on={includeMeasurements} onClick={() => setIncludeMeasurements((v) => !v)} label="Measurements" testid="share-incl-measurements" />
              <Toggle on={includeObjects} onClick={() => setIncludeObjects((v) => !v)} label="Objects" testid="share-incl-objects" />
              <Toggle on={includePhotos} onClick={() => setIncludePhotos((v) => !v)} label="Photos" testid="share-incl-photos" />
              <Toggle on={includeDocuments} onClick={() => setIncludeDocuments((v) => !v)} label="Documents" testid="share-incl-documents" />
            </Section>

            <Section title="Permissions">
              <Toggle on={allowDownload} onClick={() => setAllowDownload((v) => !v)} label="Allow PDF download" testid="share-allow-download" />
            </Section>

            <Section title="Who can open this link">
              <div className="grid grid-cols-3 gap-2" data-testid="share-access-mode">
                <Choice on={accessMode === "public_token"} onClick={() => setAccessMode("public_token")}
                        label="Anyone with link" testid="share-access-public" />
                <Choice on={accessMode === "password"} onClick={() => setAccessMode("password")}
                        label="Password" testid="share-access-password" />
                <Choice on={accessMode === "email_allowlist"} onClick={() => setAccessMode("email_allowlist")}
                        label="Email allowlist" testid="share-access-email" />
              </div>
              {accessMode === "password" && (
                <div className="mt-3 flex items-center gap-2">
                  <Lock size={14} className="text-[var(--fop-text-3)]" />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                         placeholder="Set a password (min 4 chars)"
                         data-testid="share-password"
                         className="flex-1 bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-1.5 text-sm" />
                </div>
              )}
              {accessMode === "email_allowlist" && (
                <div className="mt-3">
                  <textarea
                    rows={3}
                    value={emailListRaw}
                    onChange={(e) => { setEmailListRaw(e.target.value); setEmailErrors([]); }}
                    placeholder="alice@contractor.com, bob@cityoffulton.gov"
                    data-testid="share-email-allowlist"
                    className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm font-mono"
                  />
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-1.5"
                       data-testid="share-email-helper">
                    Only these email addresses can open this share link.
                    {emailList.length > 0 && ` · ${emailList.length} recipient${emailList.length === 1 ? "" : "s"}`}
                  </div>
                  {emailErrors.length > 0 && (
                    <div className="text-[10px] text-[var(--fop-danger)] mt-1" data-testid="share-email-errors">
                      Invalid: {emailErrors.join(", ")}
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title="Expiration">
              <div className="flex flex-wrap gap-1.5">
                {DURATIONS.map((d) => (
                  <Choice key={d.id} on={!customHours && duration === d.id}
                          onClick={() => { setDuration(d.id); setCustomHours(null); }}
                          label={d.label} testid={`share-duration-${d.id}`} />
                ))}
                <input type="number" min={1} max={24 * 365}
                       value={customHours || ""}
                       onChange={(e) => setCustomHours(Number(e.target.value) || null)}
                       placeholder="Custom hours"
                       data-testid="share-duration-custom"
                       className="w-32 bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-1.5 text-xs" />
              </div>
            </Section>

            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--fop-border)]">
              <button onClick={onClose} data-testid="share-cancel"
                      className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] px-3 py-2"
                      style={{ minHeight: 44 }}>Cancel</button>
              <button onClick={submit}
                      disabled={creating
                                 || (accessMode === "password" && password.length < 4)
                                 || (accessMode === "email_allowlist" && emailList.length === 0)}
                      data-testid="share-submit"
                      className="text-xs font-mono uppercase tracking-wider px-3 py-2 rounded bg-[var(--fop-primary)] text-black disabled:opacity-50"
                      style={{ minHeight: 44 }}>
                {creating ? "Creating…" : "Create link"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3 text-sm">
            <div className="text-[var(--fop-success)] text-sm font-medium">Share link created</div>
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl} data-testid="share-url"
                     className="flex-1 bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 font-mono text-xs text-[var(--fop-text)]" />
              <button onClick={copyLink} data-testid="share-copy"
                      className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded bg-[var(--fop-primary)] text-black"
                      style={{ minHeight: 44 }}>
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="flex gap-2">
              <a href={shareUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="share-open-preview"
                 className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded border border-[var(--fop-border)] text-[var(--fop-text)]"
                 style={{ minHeight: 44 }}>
                <ExternalLink size={14} /> Open preview
              </a>
              <button onClick={() => revoke(created.id)} data-testid="share-revoke"
                      className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded border border-[var(--fop-border)] text-[var(--fop-danger)]"
                      style={{ minHeight: 44 }}>
                <Trash2 size={14} /> Revoke
              </button>
              <button onClick={() => setCreated(null)} data-testid="share-create-another"
                      className="text-xs font-mono uppercase tracking-wider px-3 py-2 text-[var(--fop-text-3)]"
                      style={{ minHeight: 44 }}>
                Create another
              </button>
            </div>
          </div>
        )}

        {/* Existing shares list */}
        <div className="border-t border-[var(--fop-border)] px-4 py-3" data-testid="share-list">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
              Active shares · {shares.length}
            </span>
            <button onClick={reload} title="Refresh"
                    className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] p-1"
                    data-testid="share-list-refresh">
              <RefreshCw size={12} />
            </button>
          </div>
          {shares.length === 0 ? (
            <div className="text-xs text-[var(--fop-text-3)]">No active shares for this project.</div>
          ) : (
            <ul className="max-h-48 overflow-y-auto">
              {shares.map((s) => (
                <li key={s.id} className="py-1.5 border-b border-[var(--fop-border)] last:border-0 flex items-center gap-2"
                    data-testid={`share-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--fop-text)] truncate">{s.title}</div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      {s.share_type} · {s.status} · {s.view_count} views
                      {s.expires_at ? ` · exp ${new Date(s.expires_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  {s.status === "active" && (
                    <button onClick={() => revoke(s.id)} title="Revoke"
                            data-testid={`share-revoke-${s.id}`}
                            className="text-[var(--fop-text-3)] hover:text-[var(--fop-danger)] p-1">
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1.5">{title}</div>
      {children}
    </div>
  );
}
function Choice({ on, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-2 rounded border ${on ? "border-[var(--fop-primary)] bg-[rgba(249,115,22,0.08)] text-[var(--fop-primary)]" : "border-[var(--fop-border)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"}`}
            style={{ minHeight: 44 }}>
      {label}
    </button>
  );
}
function Toggle({ on, onClick, label, testid }) {
  return (
    <label data-testid={testid} className="flex items-center gap-2 py-1 cursor-pointer">
      <button onClick={onClick} type="button" aria-pressed={on}
              className={`w-9 h-5 rounded-full border transition-colors ${on ? "bg-[var(--fop-primary)] border-[var(--fop-primary)]" : "bg-[var(--fop-surface-2)] border-[var(--fop-border)]"}`}>
        <span className={`block w-4 h-4 bg-white rounded-full shadow translate-x-0 transition-transform ${on ? "translate-x-4" : ""}`} />
      </button>
      <span className="text-xs text-[var(--fop-text)]">{label}</span>
    </label>
  );
}
